import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiChatBubbleLeftRight } from "react-icons/hi2";
import { IoClose, IoSend } from "react-icons/io5";
import { SupportChatApis } from "../apis/supportChatApis";

type MessageSender = "user" | "support";

type ChatMessage = {
  id: string;
  text: string;
  sender: MessageSender;
  timestamp: Date;
};

const WELCOME_MESSAGE: ChatMessage = {
  id: "welcome",
  text: "Hi there! How can we help you today?",
  sender: "support",
  timestamp: new Date(),
};

function createMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [isOpen, messages, scrollToBottom]);

  const handleSendMessage = async () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || isSending) {
      return;
    }

    const userMessage: ChatMessage = {
      id: createMessageId(),
      text: trimmedMessage,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsSending(true);

    try {
      const response = await SupportChatApis.sendMessage({
        message: trimmedMessage,
        conversation_id: conversationId,
      });

      const reply =
        response.data?.data?.reply ||
        response.data?.message ||
        "Thanks for your message. Our team will get back to you shortly.";

      if (response.data?.data?.conversation_id) {
        setConversationId(response.data.data.conversation_id);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          text: reply,
          sender: "support",
          timestamp: new Date(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          text: "Sorry, we couldn't reach support right now. Please try again in a moment.",
          sender: "support",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end font-body">
      {isOpen && (
        <div
          className="mb-4 flex w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
          role="dialog"
          aria-label="Support chat"
        >
          <div className="flex items-center justify-between bg-[#0B7F47] px-4 py-3 text-white">
            <div>
              <h3 className="text-base font-semibold">Support Chat</h3>
              <p className="text-xs text-white/80">We typically reply in a few minutes</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-lg p-1.5 transition-colors hover:bg-white/10"
              aria-label="Close support chat"
            >
              <IoClose size={22} />
            </button>
          </div>

          <div className="flex h-[360px] flex-col gap-3 overflow-y-auto bg-[#F9FAFB] px-4 py-4">
            {messages.map((message) => {
              const isUser = message.sender === "user";

              return (
                <div
                  key={message.id}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                      isUser
                        ? "rounded-br-md bg-[#0B7F47] text-white"
                        : "rounded-bl-md border border-[#E5E7EB] bg-white text-[#111827]"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{message.text}</p>
                    <span
                      className={`mt-1 block text-[10px] ${
                        isUser ? "text-white/70" : "text-[#9CA3AF]"
                      }`}
                    >
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                </div>
              );
            })}

            {isSending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md border border-[#E5E7EB] bg-white px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-[#0B7F47] [animation-delay:-0.3s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-[#0B7F47] [animation-delay:-0.15s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-[#0B7F47]" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-[#E5E7EB] bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                rows={1}
                disabled={isSending}
                className="max-h-24 min-h-[44px] flex-1 resize-none rounded-xl border border-[#E5E7EB] px-3 py-2.5 text-sm text-[#111827] outline-none transition-colors placeholder:text-[#9CA3AF] focus:border-[#0B7F47] disabled:cursor-not-allowed disabled:bg-[#F3F4F6]"
              />
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isSending}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0B7F47] text-white transition-colors hover:bg-[#096b3c] disabled:cursor-not-allowed disabled:bg-[#0B7F47]/50"
                aria-label="Send message"
              >
                <IoSend size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0B7F47] text-white shadow-[0_4px_20px_rgba(11,127,71,0.4)] transition-transform hover:scale-105 hover:bg-[#096b3c]"
        aria-label={isOpen ? "Close support chat" : "Open support chat"}
        aria-expanded={isOpen}
      >
        {isOpen ? <IoClose size={26} /> : <HiChatBubbleLeftRight size={26} />}
      </button>
    </div>
  );
}
